<template>
  <div :class="getWrapperClass" v-if="total > 0">
    <div class="pagination-amount flex items-center">
      <span>Showing:</span>
      <span class="ml-2 font-bold">{{ meta.from }}-{{ meta.to }}</span>
      <span class="ml-2">
        of
      </span>
      <span class="ml-2 font-bold"> {{ total }}</span>
      <span class="ml-2">
        results
      </span>
      <span class="ml-6">
        Sort by
        <custom-drop-down
          class="pagination-amount__dropdown font-bold"
          @onChanged="setItemsPerPage"
          :dataset="perPageOptions"
          :label="meta.per_page"
          size="sm"
        />
      </span>
    </div>

    <nav
      class="pagination-slider"
      v-show="Math.abs(total - pageNumber * Number.parseInt(perPage)) <= total"
    >
      <a
        :class="{ disabled: pageNumber === 1 }"
        @click="prevPage"
        class="pagination-slider__prev-link"
        href="javascript:void(0);"
      >
        Prev
      </a>
      <ul class="pagination-slider__list">
        <template
          v-if="
            Math.abs(page - pageNumber) < 3 || page === pageCount || page === 1
          "
        >
          <li
            class="pagination-slider__item"
            :class="{ active: activePage(page) }"
            v-for="(page, key) in pageCount"
            :key="key"
          >
            <a href="javascript:void(0);" @click="toPage(page)">
              <span
                class="left-dot"
                v-show="page === pageCount && Math.abs(page - pageNumber) > 3"
                >...</span
              >
              {{ page }}
              <span
                class="right-dot"
                v-show="page === 1 && Math.abs(page - pageNumber) > 3"
                >...</span
              >
            </a>
          </li>
        </template>
      </ul>
      <a
        :class="{ disabled: pageNumber >= pageCount }"
        @click="nextPage"
        class="pagination-slider__next-link"
        href="javascript:void(0);"
      >
        Next
      </a>
    </nav>
  </div>
</template>

<script>
import CustomDropDown from "@/components/common/controls/CustomDropDown.vue";

export default {
  props: {
    meta: Object,
    withoutPaddings: Boolean
  },
  name: "Pagination",
  components: {
    "custom-drop-down": CustomDropDown
  },
  data() {
    return {
      page: this.$route.query.page ? Number.parseInt(this.$route.query.page) : 1
    };
  },

  computed: {
    metaData() {
      return this.meta;
    },
    pageCount() {
      return this.metaData.last_page;
    },
    perPage() {
      return this.metaData.per_page;
    },
    pageNumber() {
      return this.$route.query.page
        ? Number.parseInt(this.$route.query.page)
        : 1;
    },
    total() {
      return this.metaData.total;
    },
    perPageOptions() {
      return [5, 10, 20, 50, 100, 200, 500];
    },
    getWrapperClass() {
      if (this.withoutPaddings) {
        return "pagination";
      } else {
        return "pagination px-4 py-2";
      }
    }
  },
  watch: {
    pageNumber(page) {
      this.page = page;
    }
  },
  methods: {
    nextPage() {
      this.page++;
      this.$emit("nextPage", this.page, this.perPage);
    },
    prevPage() {
      this.page--;
      this.$emit("prevPage", this.page, this.perPage);
    },
    toPage(page) {
      this.page = page;
      this.$emit("toPage", page, this.perPage);
    },
    activePage(page) {
      return page === this.pageNumber;
    },
    setItemsPerPage(value) {
      const page = Math.ceil(this.metaData.to / value);
      this.$emit("perPage", page, Number.parseInt(value));
    }
  }
};
</script>

<style lang="scss" scoped>
.pagination {
  --primary: #f75f39;
  --white: rgba(255, 255, 255, 1);
  --gray: rgba(107, 107, 116, 1);
  --lightgray: rgba(142, 142, 151, 1);
  --lightgraydisabled: rgb(211, 211, 236);
  --pale: rgba(252, 252, 253, 1);

  a {
    text-decoration: none;
    color: var(--lightgray);
  }

  display: flex;
  flex-flow: row wrap;
  align-items: center;
  justify-content: space-between;
  color: var(--gray);
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;

  /** AMOUNT */
  .pagination-amount {
    span {
      display: flex;
      flex-direction: row;
      align-items: center;
    }

    &__range {
      font-weight: 600;
      margin: 0 16px 0 8px;
    }

    &__dropdown {
      margin-left: 1rem;
      margin-right: 1rem;
      font-weight: 600;
    }

    &__picker {
      font-size: 1px;
      color: #fff;
      height: 14px;
      border: none;
      cursor: pointer;
      font-weight: 600;
      margin: 0 8px 0 5px;
      color: var(--gray);
      background: #fff;

      option {
        font-size: 1rem;
      }
    }

    @media screen and (max-width: 768px) {
      display: none;
    }
  }

  /** SLIDER */
  .pagination-slider {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 10px;
    font-weight: 600;

    .disabled {
      color: var(--lightgraydisabled);
      pointer-events: none;
    }

    &__prev-link,
    &__next-link {
      display: flex;
      height: 35px;
      justify-content: center;
      align-items: center;
      transition: color 0.3s ease;

      &:hover,
      &:focus {
        color: var(--primary);
      }

      @media screen and (max-width: 340px) {
        display: none;
      }
    }

    &__prev-link {
      margin-right: 22px;
    }

    &__next-link {
      margin-left: 22px;
    }

    &__list {
      display: flex;
      margin: 0;
      padding: 0;
    }

    &__item {
      display: inline;
      margin: 0 4px;

      a {
        display: flex;
        width: 35px;
        height: 35px;
        justify-content: center;
        align-items: center;
        color: var(--gray);
        border-radius: 8px;
        transition: background-color 0.3s ease, color 0.3s ease;

        &:hover,
        &:focus {
          background-color: var(--pale);
          color: var(--primary);
        }

        .left-dot {
          padding-right: 20px;
        }

        .right-dot {
          padding-left: 20px;
        }
      }

      &.active a {
        color: var(--white);
        background-color: var(--primary);
      }

      &.disabled {
        pointer-events: none;
      }
    }

    @media screen and (max-width: 768px) {
      margin: auto;
    }
  }
}
.pagination.no-lr-paddings {
  padding-left: 0;
  padding-right: 0;

  .pagination-amount {
    margin-left: 0;
  }
}
</style>
